import type { ItemType, Perm } from "./types";

/** Field template: [label, secret?, multiline?] */
type Tpl = [string, boolean?, boolean?];

export const TYPES: Record<ItemType, { label: string; icon: string; f: Tpl[] }> = {
  login: { label: "Login", icon: "key-round", f: [["Username"], ["Password", true], ["Website"]] },
  note: { label: "Secure note", icon: "sticky-note", f: [["Note", false, true]] },
  api: { label: "API key", icon: "code", f: [["Key ID"], ["Secret", true], ["Environment"]] },
  env: { label: "Environment variables", icon: "terminal", f: [["Variables", true, true]] },
  db: { label: "Database", icon: "database", f: [["Host"], ["Port"], ["Database"], ["Username"], ["Password", true]] },
  card: { label: "Card / bank", icon: "credit-card", f: [["Name on card"], ["Number", true], ["Expiry"], ["CVC", true]] },
  licence: { label: "Licence", icon: "ticket", f: [["Licensed to"], ["Licence key", true], ["Seats"], ["Renews"]] },
  totp: { label: "2FA code", icon: "timer", f: [["Account"], ["Setup key", true]] },
  file: { label: "File", icon: "paperclip", f: [["Description"]] },
};

export const TYPE_ORDER = Object.keys(TYPES) as ItemType[];

/** Labels whose values read better in JetBrains Mono. */
export const MONO_KEYS = new Set([
  "Host", "Port", "Key ID", "Database", "Website", "Number", "Expiry", "Licence key", "Setup key", "Username", "Client ID",
]);

export type ClientPerm = Exclude<Perm, "owner">;
export const PERMS: ClientPerm[] = ["view", "edit", "add"];
export const PERM_LABEL: Record<ClientPerm, string> = {
  view: "View only",
  edit: "Can edit",
  add: "Can add only",
};
export const PERM_DESC: Record<ClientPerm, string> = {
  view: "Can see and copy what’s in the vault",
  edit: "Can see, add and change items",
  add: "Can add credentials but not see existing ones",
};

export const CAT_ICONS = [
  "folder", "mail", "code", "server", "globe", "cloud", "database", "shopping-bag", "credit-card", "shield-check", "building-2", "stethoscope",
];

export const REMIND_DAYS = [7, 14, 30, 60];

export const LINK_EXPIRY: Record<string, number> = { "1 hour": 3600, "24 hours": 86_400, "7 days": 604_800 };
export const REQUEST_EXPIRY: Record<string, number> = { "24 hours": 86_400, "7 days": 604_800, "30 days": 2_592_000 };
export const LOCK_AFTER: Record<string, number | null> = {
  "1 minute": 60_000,
  "5 minutes": 300_000,
  "15 minutes": 900_000,
  "1 hour": 3_600_000,
  Never: null,
};

/** Subtitle for a list row: the first plain single-line value, else the type. */
export function subtitleFor(type: ItemType, fields: { v: string; s?: boolean }[], fileName?: string): string {
  if (type === "file" && fileName) return fileName;
  const first = fields.find((f) => !f.s && f.v.trim());
  return first ? first.v.split("\n")[0]!.slice(0, 80) : TYPES[type].label;
}
